import * as React from "react";
import { Card, Tooltip } from "antd";
import { ContainerSpec, Workload } from "../../../schema/types/workload/entity";
import { CardContent } from "../../../components/antd/card/CardContent";
import { tagLinkUrlPrefixes } from "../../../services/utils";
import { TagLinksTable } from "../../../components/detail/tagLinksTable";
import { Gvc } from "../../../schema/types/gvc/entity";
import { Deployment } from "../../../schema/types/workload/deployment";
import { workloadHelpers } from "../../../mst/kinds/workload.helpers";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { Activity } from "react-feather";
import clsx from "clsx";
import { linksOf } from "../../../services/cpln";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { useGetIdentityQuery } from "../../../store/api/identityApi";
import { Table } from "../../../newcomponents/table/table";
import { CopyButtonWithApproval } from "../../domain/create/copyButtonWithApproval";
import objectHash from "object-hash";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { FormItem } from "../../../components/antd/form/FormItem";
import { FormItemValue } from "../../../components/antd/form/FormItemValue";
import { DetectedTags } from "../../../components/detail/detectedTags";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpTextArea } from "../../gvc/testNewForm/CpTextArea";
import { CpLabelDirtyIndicator } from "../../gvc/testNewForm/CpLabelDirtyIndicator";
import { InternalLink } from "../../../newcomponents/table/components/InternalLink";
import { WorldMapPanel } from "./WorldMapPanel";

// Tooltip that only shows when content is truncated
const TruncatedTooltip: React.FC<{ children: React.ReactNode; title: string }> = ({ children, title }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      const el = ref.current;
      if (el) {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => resizeObserver.disconnect();
  }, [title]);

  return (
    <Tooltip title={isTruncated ? title : undefined}>
      <div ref={ref} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {children}
      </div>
    </Tooltip>
  );
};

interface Endpoint {
  label: string;
  url: string;
  internal: boolean;
}

interface Props {
  workload: Workload;
  gvc?: Gvc;
  deployments: Deployment[];
}
export const Summary: React.FC<Props> = ({ workload, gvc, deployments }) => {
  const selfLink = linksOf(workload).self!;

  const org = useAppSelector(selectOrg);
  const domains = useAppSelector((state) => state.rest.domainItems);
  const domainHash = React.useMemo(() => objectHash(domains), [JSON.stringify(domains)]);

  const { data: identity, isError: identityAccessError } = useGetIdentityQuery(workload.spec.identityLink!, {
    skip: !workload.spec.identityLink,
  });

  const identityName = workload.spec.identityLink
    ? parseLink(workload.spec.identityLink, { useInputCtx: true }).name
    : null;
  const identityUrl = workload.spec.identityLink
    ? `/console/org/${org}/gvc/${parseLink(selfLink, { useInputCtx: true }).gvc}/identity/${identityName}`
    : null;

  const health = React.useMemo(() => {
    return workloadHelpers.getHealthFromDeployments(deployments, workload);
  }, [deployments, workload]);

  const showGlobalEndpoint = React.useMemo(() => {
    let result = true;
    if (
      !!workload.status?.endpoint &&
      workload.status?.endpoint === workload.status?.canonicalEndpoint &&
      !(workload.spec.type === "serverless" && gvc?.spec?.loadBalancer?.dedicated)
    ) {
      result = false;
    }
    return result;
  }, [workload, gvc]);

  const showCanonicalEndpoints = React.useMemo(() => {
    let result = true;
    if (
      !!workload.status?.canonicalEndpoint &&
      workload.spec.type === "serverless" &&
      gvc?.spec?.loadBalancer?.dedicated
    ) {
      result = false;
    }
    return result;
  }, [workload, gvc]);

  const endpoints: Endpoint[] = React.useMemo(() => {
    let endpoints: Endpoint[] = [];
    if (!!workload.status?.internalName) {
      endpoints.push({ url: workload.status?.internalName, label: "Workload Internal Name", internal: true });
    }
    for (const replicaInternalName of workload.status?.replicaInternalNames || []) {
      endpoints.push({ url: replicaInternalName, label: "Replica Internal Name", internal: true });
    }
    if (showGlobalEndpoint) {
      endpoints.push({ url: workload.status!.endpoint!, label: "GVC Domain Endpoint", internal: false });
    }
    if (showCanonicalEndpoints) {
      endpoints.push({
        url: workload.status!.canonicalEndpoint!,
        label: "Canonical Endpoint (Global)",
        internal: false,
      });
    }

    for (const domain of domains) {
      for (const domainEndpoint of domain.status.endpoints || []) {
        if (domainEndpoint.workloadLink === selfLink) {
          endpoints.push({ url: domainEndpoint.url, label: domain.name, internal: false });
        }
      }
    }

    for (const indexStr in workload.status?.loadBalancer || []) {
      const index = Number(indexStr);
      if (index === 0) {
        continue;
      }
      const loadBalancerEndpoint = workload.status!.loadBalancer[index];
      endpoints.push({ url: loadBalancerEndpoint.url, label: loadBalancerEndpoint.origin, internal: false });
    }
    // TODO what is the reason for this?
    if ((workload.status?.loadBalancer || []).length > 1) {
      for (const lbEndpoint of workload.status?.loadBalancer || []) {
        endpoints.push({ url: lbEndpoint.url, label: lbEndpoint.origin, internal: false });
      }
    }
    return endpoints;
  }, [domainHash]);

  const endpointsHash = React.useMemo(() => objectHash(endpoints), [endpoints]);

  const hasTagLinks =
    Object.entries(workload.tags || {}).filter(([_, value]) =>
      tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)),
    ).length > 0;

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card id={"summary"}>
          <CardContent>
            <div className="flex gap-8 mb-10">
              <WorldMapPanel
                workload={workload}
                gvc={gvc}
                deployments={deployments}
                features={{ showAllOrgLocations: true }}
              />
              <div className="flex flex-col gap-4" style={{ paddingTop: 8 }}>
                <FormItem lastItem>
                  <FormLabel>Type</FormLabel>
                  <FormItemValue>{workloadHelpers.getTypeText(workload)}</FormItemValue>
                </FormItem>
                <FormItem lastItem>
                  <FormLabel>Health</FormLabel>
                  {!health.syncFailed && health.isLatestReady ? (
                    <div className="flex items-center gap-1">
                      <Activity size={16} className={clsx("color-action")} />
                      <FormItemValue>{health.text}</FormItemValue>
                    </div>
                  ) : (
                    <Tooltip title={health.syncFailed ? "Sync Failed" : `Not Latest Version`}>
                      <div className="flex items-center gap-1" style={{ width: "max-content" }}>
                        <Activity
                          size={16}
                          className={clsx("feather-icon", {
                            "color-action": health.isLatestReady,
                            "color-warning": health.isReady,
                            "color-danger": !health.isReady,
                          })}
                        />
                        <FormItemValue>{health.text}</FormItemValue>
                      </div>
                    </Tooltip>
                  )}
                </FormItem>
                <FormItem lastItem>
                  <FormLabel>Suspended</FormLabel>
                  <FormItemValue>{workload.spec.defaultOptions?.suspend ? "Yes" : "No"}</FormItemValue>
                </FormItem>
                <FormItem lastItem>
                  <FormLabel>Ready / Total Replicas</FormLabel>
                  <FormItemValue>{workloadHelpers.getReadyReplicas(deployments, workload)}</FormItemValue>
                </FormItem>
                <FormItem lastItem>
                  <FormLabel>Min / Max Scale</FormLabel>
                  <FormItemValue>
                    {workload.spec.defaultOptions?.autoscaling?.minScale ?? 1} /{" "}
                    {workload.spec.defaultOptions?.autoscaling?.maxScale ?? 1}
                  </FormItemValue>
                </FormItem>
                <FormItem lastItem>
                  <FormLabel>Identity</FormLabel>
                  <FormItemValue>
                    {workload.spec.identityLink ? (
                      identity && !identityAccessError ? (
                        <InternalLink label={identityName!} to={identityUrl!} />
                      ) : (
                        <div>{identityName}</div>
                      )
                    ) : (
                      <div>No workload identity is set</div>
                    )}
                  </FormItemValue>
                </FormItem>
              </div>
            </div>
            <CpFormItem>
              <CpLabel name={`description`}>
                <FormLabel>Description</FormLabel>
                <CpLabelDirtyIndicator name={`description`} />
              </CpLabel>
              <CpTextArea autoSize name={`description`} style={{ maxWidth: 600 }} />
              <CpErrorWarning name={`description`} />
            </CpFormItem>
            <div className="mb-10">
              <Table<ContainerSpec>
                data={workload.spec.containers}
                hideSettings
                title={() => <div>Containers</div>}
                styles={{ header: { marginBottom: 0 } }}
                columns={[
                  {
                    id: "name",
                    label: "Name",
                    cell: (props) => {
                      const name = props.row.original.name;
                      return <TruncatedTooltip title={name}>{name}</TruncatedTooltip>;
                    },
                  },
                  {
                    id: "image",
                    label: "Image",
                    cell: (props) => {
                      const image = props.row.original.image;
                      const displayImage = image.startsWith(`/org/${org}/image/`)
                        ? image.replace(`/org/${org}/image/`, "")
                        : image;
                      return (
                        <TruncatedTooltip title={displayImage}>
                          {image.startsWith(`/org/${org}/image/`) && !image.includes("@sha256:") ? (
                            <InternalLink label={displayImage} to={`/console${image}`} />
                          ) : (
                            displayImage
                          )}
                        </TruncatedTooltip>
                      );
                    },
                  },
                  {
                    id: "cpu",
                    label: "CPU",
                    cell: (props) => {
                      const c = props.row.original;
                      const minMax = workload.spec.type === "stateful" || !!workload.spec.defaultOptions?.capacityAI;
                      let text = c.cpu || "";
                      if (minMax && c.minCpu) {
                        text = `${c.minCpu} - ${c.cpu}`;
                      } else if (minMax) {
                        text = `Max ${c.cpu}`;
                      }
                      return <TruncatedTooltip title={text}>{text}</TruncatedTooltip>;
                    },
                  },
                  {
                    id: "memory",
                    label: "Memory",
                    cell: (props) => {
                      const c = props.row.original;
                      const minMax = workload.spec.type === "stateful" || !!workload.spec.defaultOptions?.capacityAI;
                      let text = c.memory || "";
                      if (minMax && c.minMemory) {
                        text = `${c.minMemory} - ${c.memory}`;
                      } else if (minMax) {
                        text = `Max ${c.memory}`;
                      }
                      return <TruncatedTooltip title={text}>{text}</TruncatedTooltip>;
                    },
                  },
                  {
                    id: "gpu",
                    label: "GPU",
                    cell: (props) => {
                      const c = props.row.original;
                      let text = "-";
                      if (c.gpu?.custom) {
                        text = `${c.gpu.custom.quantity}x Custom`;
                      } else if (c.gpu?.nvidia) {
                        text = `${c.gpu.nvidia.quantity}x ${c.gpu.nvidia.model}`;
                      }
                      return <TruncatedTooltip title={text}>{text}</TruncatedTooltip>;
                    },
                  },
                  {
                    id: "ports",
                    label: "Ports",
                    cell: (props) => {
                      const ports = props.row.original.ports || [];
                      if (ports.length === 0) return <div>-</div>;
                      const text = ports.map((p) => `${p.protocol.toUpperCase()}:${p.number}`).join(", ");
                      return <TruncatedTooltip title={text}>{text}</TruncatedTooltip>;
                    },
                  },
                ]}
                tableId="workload-summary-endpoints"
              />
            </div>
            <div key={endpointsHash} className="mb-10">
              <Table<Endpoint>
                data={endpoints}
                hideSettings
                title={() => <div>Endpoints</div>}
                styles={{ header: { marginBottom: 0 } }}
                columns={[
                  { id: "label", label: "Label" },
                  {
                    id: "url",
                    label: "URL",
                    cell: (props) => {
                      const e = props.row.original;
                      if (e.internal) {
                        return (
                          <div className="flex items-center gap-1">
                            <div>{e.url}</div>
                            <CopyButtonWithApproval value={e.url} />
                          </div>
                        );
                      }

                      return <ExternalLink label={e.url} url={e.url} />;
                    },
                  },
                ]}
                tableId="workload-summary-endpoints"
              />
            </div>
            <DetectedTags
              value={workload.tags}
              detectValue={(tags, key) => {
                const hasTag = tags.hasOwnProperty(key);
                if (!hasTag) {
                  return false;
                }
                if (key === "cpln/ignore") {
                  const value = tags[key];
                  return String(value) === "true";
                }
                return true;
              }}
              detectionMap={{
                "cpln/ignore": "Stops processing changes to this object",
                "cpln/disableServiceMeshInboundPort": "Ports to omit from service mesh, comma separated",
                "cpln/disableServiceMeshOutboundPort": "Ports to omit from service mesh, comma separated",
              }}
              pluralLabel="Tags"
            />
            {hasTagLinks ? (
              <TagLinksTable
                tableId="workload-summary-tag-links"
                tags={Object.entries(workload.tags || {})
                  .filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
                  .map(([key, value]) => ({ key, value }))}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
